"use client";

import { useEffect, useMemo, useState } from "react";
import { useParams } from "next/navigation";
import { Mail, ArrowRight, Command, RefreshCw, Check } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { apiCall } from "@/utils/api";
import { routing } from "@/i18n/routing";
import { useUser } from "@/context/UserProvider";

type Step = "email" | "otp";

interface ConsentState {
	terms: boolean;
	privacy: boolean;
	oferta: boolean;
}

export default function AuthCard() {
	const t = useTranslations();
	const { refreshUserData } = useUser();
	const params = useParams<{ locale?: string }>();
	const locale = (params?.locale as (typeof routing.locales)[number]) ?? routing.defaultLocale;
	const [step, setStep] = useState<Step>("email");
	const [email, setEmail] = useState("");
	const [code, setCode] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const COUNTDOWN_SEC = 30;
	const [consents, setConsents] = useState<ConsentState>({
		terms: false,
		privacy: false,
		oferta: false,
	});
	const allConsentsChecked = useMemo(() => consents.terms && consents.privacy && consents.oferta, [consents]);

	useEffect(() => {
		if (cooldown <= 0) return;
		const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
		return () => clearInterval(timer);
	}, [cooldown]);

	const canResend = useMemo(() => cooldown <= 0, [cooldown]);
	const errorMessage = (err: unknown, fallbackKey: string) =>
		typeof err === "object" && err && "message" in err && typeof (err as { message?: unknown }).message === "string"
			? (err as { message: string }).message
			: t(fallbackKey);

	const handleSendCode = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!allConsentsChecked) {
			setError(t("auth_consent_required"));
			return;
		}
		setError("");
		setLoading(true);
		try {
			await apiCall("/user/auth-code/", { email }, "POST", locale);
			setStep("otp");
			setCooldown(COUNTDOWN_SEC);
		} catch (err: unknown) {
			setError(errorMessage(err, "auth_error_send"));
		} finally {
			setLoading(false);
		}
	};

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setLoading(true);
		try {
			await apiCall("/user/session/", { email, code }, "POST", locale);
			// Refresh user data after successful verification
			await refreshUserData();
		} catch (_err: unknown) {
			setError(t("auth_error_code"));
		} finally {
			setLoading(false);
		}
	};

	const handleResend = async () => {
		if (!canResend) return;
		setError("");
		setLoading(true);
		try {
			await apiCall("/user/auth-code/", { email }, "POST", locale);
			setCooldown(COUNTDOWN_SEC);
		} catch (err: unknown) {
			setError(errorMessage(err, "auth_error_send"));
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="relative z-10 w-full max-w-md px-6">
			<div className="text-center mb-8">
				<div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 border border-primary/50 text-white shadow-[0_0_30px_rgba(10,132,255,0.3)] mb-4 backdrop-blur-md">
					<Command size={32} className="text-primary" />
				</div>
			</div>

			<div className="glass-card rounded-[2.5rem] p-8 md:p-10">
				<form onSubmit={step === "email" ? handleSendCode : handleVerify} className="space-y-4">
					<div className="space-y-1.5">
						<label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ml-1">
							{step === "email" ? t("email_login") : t("auth_enter_code")}
						</label>
						<div className="relative">
							<Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
							{step === "email" ? (
								<input
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="hello@example.com"
									className="w-full bg-secondary/50 border border-transparent rounded-xl py-3.5 pl-12 pr-4 text-foreground font-medium focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-muted-foreground/50"
									required
								/>
							) : (
								<input
									type="text"
									value={code}
									onChange={(e) => setCode(e.target.value)}
									placeholder={t("auth_code_placeholder")}
									className="w-full bg-secondary/50 border border-transparent rounded-xl py-3.5 pl-12 pr-4 text-foreground font-medium focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-muted-foreground/50"
									required
								/>
							)}
						</div>
					</div>

					{error && <p className="text-sm text-red-500 bg-red-50 border border-red-100 rounded-xl p-3">{error}</p>}

					{step === "email" && (
						<div className="space-y-3 py-2">
							<p className="text-sm font-medium text-foreground mb-3">{t("auth_consent_title")}</p>
							
							<label className="flex items-start gap-3 cursor-pointer group">
								<div className={"relative flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 transition-all flex items-center justify-center " + (consents.terms ? "bg-primary border-primary" : "border-muted-foreground/30 group-hover:border-muted-foreground/50")}>
									{consents.terms && <Check size={12} className="text-white" />}
									<input
										type="checkbox"
										checked={consents.terms}
										onChange={(e) => setConsents(prev => ({ ...prev, terms: e.target.checked }))}
										className="sr-only"
									/>
								</div>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{t("auth_consent_terms")}{" "}
									<Link href="/terms" className="text-primary hover:underline" target="_blank">{t("auth_consent_terms_link")}</Link>
								</span>
							</label>

							<label className="flex items-start gap-3 cursor-pointer group">
								<div className={"relative flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 transition-all flex items-center justify-center " + (consents.privacy ? "bg-primary border-primary" : "border-muted-foreground/30 group-hover:border-muted-foreground/50")}>
									{consents.privacy && <Check size={12} className="text-white" />}
									<input
										type="checkbox"
										checked={consents.privacy}
										onChange={(e) => setConsents(prev => ({ ...prev, privacy: e.target.checked }))}
										className="sr-only"
									/>
								</div>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{t("auth_consent_privacy")}{" "}
									<Link href="/privacy" className="text-primary hover:underline" target="_blank">{t("auth_consent_privacy_link")}</Link>
								</span>
							</label>

							<label className="flex items-start gap-3 cursor-pointer group">
								<div className={"relative flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 transition-all flex items-center justify-center " + (consents.oferta ? "bg-primary border-primary" : "border-muted-foreground/30 group-hover:border-muted-foreground/50")}>
									{consents.oferta && <Check size={12} className="text-white" />}
									<input
										type="checkbox"
										checked={consents.oferta}
										onChange={(e) => setConsents(prev => ({ ...prev, oferta: e.target.checked }))}
										className="sr-only"
									/>
								</div>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{t("auth_consent_oferta")}{" "}
									<Link href="/oferta" className="text-primary hover:underline" target="_blank">{t("auth_consent_oferta_link")}</Link>
								</span>
							</label>
						</div>
					)}

					<button
						type="submit"
						disabled={loading || (step === "email" && !allConsentsChecked)}
						className="w-full bg-primary text-white rounded-xl py-4 font-bold text-lg hover:bg-primary/90 transition-all shadow-[0_4px_14px_0_rgba(10,132,255,0.39)] hover:shadow-[0_6px_20px_rgba(10,132,255,0.23)] active:opacity-70 active:scale-[0.98] flex items-center justify-center gap-2 mt-2 disabled:opacity-60 disabled:pointer-events-none"
					>
						{step === "email" ? t("auth_send_code") : t("auth_verify_code")}
						{loading ? <RefreshCw size={18} className="animate-spin" /> : <ArrowRight size={20} />}
					</button>
				</form>

				{step === "otp" && (
					<div className="mt-4 flex flex-col gap-3">
						<button
							type="button"
							onClick={() => {
								setStep("email");
								setCode("");
								setError("");
								setCooldown(0);
							}}
							className="text-sm font-semibold text-muted-foreground hover:text-primary transition-colors self-start"
						>
							{t("auth_change_email")}
						</button>
						<div className="flex items-center gap-3 text-sm text-slate-500">
							{canResend ? (
								<button
									type="button"
									onClick={handleResend}
									className="text-primary font-semibold hover:underline inline-flex items-center gap-2"
								>
									<RefreshCw size={16} />
									{t("auth_resend")}
								</button>
							) : (
								<span className="text-muted-foreground">{t("auth_resend_in", { seconds: cooldown })}</span>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
